// ============================================================
// SEEDED RNG — deterministic pseudo-random number generator
// ============================================================
//
// Mulberry32. Small (≈10 lines of math), fast, and gives a full
// 2^32 period. Not cryptographic, but perfectly fine for gameplay
// determinism / replay purposes.
//
// Usage:
//
//   const rng = new SeededRng(12345);
//   rng.nextInt(1, 5);   // inclusive both ends — analogue of ri()
//   rng.nextFloat();     // [0, 1)
//   rng.nextUint();      // full 32-bit unsigned
//
// Deriving multiple streams from one master seed:
//
//   const master = new SeededRng(masterSeed);
//   const seedA = master.nextUint();
//   const seedB = master.nextUint();
//   const rngA  = new SeededRng(seedA);
//   const rngB  = new SeededRng(seedB);
//
// This module lives under server/ so both the client bundle and
// the Node server can import from the same source of truth. Keep
// it free of DOM / Node-only APIs.

export class SeededRng {
  private s: number;

  constructor(seed: number) {
    // Normalise to a 32-bit unsigned integer.
    // Zero seeds are valid for Mulberry32 but produce a long run of
    // zeros at the start, so nudge them.
    this.s = (seed >>> 0) || 0x9e3779b9;
  }

  /** Full 32-bit unsigned integer. */
  nextUint(): number {
    this.s = (this.s + 0x6d2b79f5) >>> 0;
    let t = this.s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return (t ^ (t >>> 14)) >>> 0;
  }

  /** Uniform float in [0, 1). */
  nextFloat(): number {
    return this.nextUint() / 0x100000000;
  }

  /** Uniform integer in [a, b] inclusive. Mirrors utils.ri() contract. */
  nextInt(a: number, b: number): number {
    return a + Math.floor(this.nextFloat() * (b - a + 1));
  }

  /** Snapshot of internal state — useful for save/load or debugging. */
  getState(): number {
    return this.s;
  }

  /** Restore from a prior snapshot. */
  setState(s: number): void {
    this.s = s >>> 0;
  }
}

/**
 * Pick a random 32-bit seed from the non-deterministic world.
 * Use ONLY at the outer edge (e.g. when a new battle begins and we
 * haven't received a server-issued seed yet). Everything downstream
 * should consume a SeededRng so replays stay reproducible.
 */
export function randomSeed(): number {
  return (Math.random() * 0x100000000) >>> 0;
}
